import React, { useCallback, useMemo, useState } from 'react';
import { View, FlatList, Platform, StyleSheet } from 'react-native';
import emoji from 'emoji-datasource';
import { FlashList } from '@shopify/flash-list';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import { Spacer } from 'design-system/Spacer';
import SearchBar from 'design-system/SearchBar';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import EmojiSelector, { charFromEmojiObject, } from 'features/emoji-picker/components/EmojiSelector';
import { useKeyboard } from 'hooks/useKeyboard';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import EmojiItem from './EmojiItem';
import { isWeb } from '../../constants';
const keyExtractor = (emoji) => emoji.unified;
export const EmojiPickerModalContent = ({ onEmojiSelected, defaultSelectedEmoji, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { bottom } = useSafeAreaInsets();
    const { deviceWidth: width, deviceHeight: height } = useAppFrameDimensions();
    const { keyboardHeight } = useKeyboard();
    const isLandscape = width > height;
    const [searchTerm, setSearchTerm] = useState('');
    const onChangeSearchTerm = useCallback((searchTerm) => {
        setSearchTerm(searchTerm);
    }, []);
    const [selectedEmoji, setSelectedEmoji] = useState(defaultSelectedEmoji || '');
    const searchEmojis = useMemo(() => searchTerm.trim().length
        ? emoji.filter((emoji) => emoji.short_name.includes(searchTerm.toLowerCase()))
        : emoji, [searchTerm]);
    const isKeyboardOpen = Boolean(keyboardHeight);
    const onPressSave = useCallback(() => {
        Modal.hide();
        onEmojiSelected(selectedEmoji);
    }, [onEmojiSelected, selectedEmoji]);
    const renderSearchItem = useCallback(({ item: e, index }) => (<View style={index < searchEmojis.length - 1 && styles.marginRight}>
        <EmojiItem item={e} key={e.unified} onLightBg={!isWeb} onSelected={setSelectedEmoji} isSelected={charFromEmojiObject(e) === selectedEmoji}/>
      </View>), [searchEmojis.length, selectedEmoji, styles.marginRight]);
    const Content = useMemo(() => {
        if (isKeyboardOpen || searchTerm) {
            if (!searchEmojis.length) {
                return (<View style={styles.noResultsContainer}>
            <Text Text-16 Secondary>
              No results
            </Text>
          </View>);
            }
            const baseProps = {
                keyExtractor,
                horizontal: true,
                data: searchEmojis,
                scrollEventThrottle: 16,
                renderItem: renderSearchItem,
                showsHorizontalScrollIndicator: false,
                contentContainerStyle: styles.searchContent,
                keyboardShouldPersistTaps: 'handled',
            };
            return (<View style={styles.flashListContainer}>
          {isWeb ? (<FlatList {...baseProps}/>) : (<FlashList {...baseProps} extraData={selectedEmoji + searchEmojis.length}/>)}
        </View>);
        }
        return (<>
        <EmojiSelector selected={selectedEmoji} onSelected={setSelectedEmoji} categoryListStyle={styles.categoryContainer}/>
        <Spacer h={16}/>
      </>);
    }, [
        isKeyboardOpen,
        renderSearchItem,
        searchEmojis,
        searchTerm,
        selectedEmoji,
        styles.categoryContainer,
        styles.flashListContainer,
        styles.noResultsContainer,
        styles.searchContent,
    ]);
    const addBottomMargin = useMemo(() => {
        const shouldAddBottomMargin = isKeyboardOpen &&
            ((Platform.OS === 'ios' && (!Platform.isPad || isLandscape)) ||
                Platform.OS === 'android');
        if (!shouldAddBottomMargin) {
            return null;
        }
        return <View style={{ height: keyboardHeight - bottom }}/>;
    }, [isKeyboardOpen, isLandscape, keyboardHeight, bottom]);
    return (<View>
      <SearchBar onChangeSearchTerm={onChangeSearchTerm} textFieldBackgroundColor={isWeb ? colors.cards.onLight : colors.background.dark}/>
      {Content}
      <Button brand title="Save" onPress={onPressSave} style={[styles.button, isKeyboardOpen && styles.buttonWithMarginBottom]} disabled={!selectedEmoji}/>
      {addBottomMargin}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    searchContent: {
        paddingHorizontal: rem(16),
    },
    button: {
        marginHorizontal: rem(16),
    },
    buttonWithMarginBottom: {
        marginBottom: rem(16),
    },
    categoryContainer: {
        marginBottom: 0,
        marginTop: 0,
        ...(isWeb
            ? {}
            : {
                backgroundColor: colors.background.dark,
            }),
        shadowColor: undefined,
        shadowRadius: 0,
        shadowOpacity: 0,
        elevation: 0,
    },
    noResultsContainer: {
        flex: 1,
        height: rem(44),
        alignItems: 'center',
        marginVertical: rem(16),
        ...(isWeb
            ? {
                marginBottom: rem(36),
            }
            : {}),
    },
    flashListContainer: {
        paddingBottom: rem(16),
    },
    marginRight: {
        marginRight: rem(16),
    },
}));
const EmojiPickerModal = {
    show: (emoji, onSelectEmoji, onCloseModal) => {
        Modal.show(<EmojiPickerModalContent defaultSelectedEmoji={emoji} onEmojiSelected={onSelectEmoji}/>, {
            title: 'Choose Emoji',
            onBackdropPress: onCloseModal,
            onSwipeComplete: onCloseModal,
            containerStyle: styles.containerStyle,
        });
    },
    hide: () => {
        Modal.hide();
    },
};
const styles = StyleSheet.create({
    containerStyle: {
        paddingHorizontal: 0,
    },
});
export default EmojiPickerModal;
